
// This file contains type definitions for the dashboard components

export type CalendarSyncSettings = {
  enabled: boolean;
  syncBirthdays: boolean;
  syncAnniversaries: boolean;
  syncCustomEvents: boolean;
};

export type ReminderChannel = "email" | "sms" | "app" | "push";

export type GiftingStyle = "Practical" | "Sentimental" | "Experiences" | "Luxury" | "Handmade";

// Define the type for TimeZone mapping
export type TimeZoneMapping = {
  [country: string]: string;
};

// Define relationship types and other enums
export type RelationshipType = "Spouse" | "Partner" | "Friend" | "Family" | "Professional" | "Colleague" | "Client" | "Mentor" | "Parent" | "Sibling" | "Other";
export type AnniversaryType = "Wedding" | "Dating" | "First Met" | "Work" | "Other";
export type ParentType = "Parent" | "Guardian" | "Mentor" | "Other";
export type FriendContext = "School" | "Work" | "Hobby" | "Travel" | "Online" | "Other";
export type CommunicationStyle = "Email" | "Phone" | "Text" | "Video" | "In Person" | "Social Media";
export type LoveLanguage = "Words of Affirmation" | "Quality Time" | "Receiving Gifts" | "Acts of Service" | "Physical Touch";
export type InteractionType = "conversation" | "event" | "help" | "reconnection" | "personal" | "note";
export type DashboardTab = "overview" | "suggestions" | "contacts" | "reminders";

export interface CustomField {
  id: string;
  label: string;
  value: string;
  isDate?: boolean;
  fieldType?: string; // Added fieldType property
}

export interface ContactFormValues {
  name: string;
  email?: string;
  phone?: string;
  relationshipType?: string;
  customRelationshipLabel?: string;
  notes?: string;
  birthdate?: Date;
  photoUrl?: string;
  importantDates?: Array<{ id: string; type: string; label: string; date: string }>;
  customFields?: Array<{ id: string; label: string; value: string; isDate?: boolean; fieldType?: string }>;
  avatarFile?: File; // Added for file uploads
  tags?: Array<{ id?: string; label: string; type?: string }>;
  isFavorite?: boolean;
  // Extended fields for social media
  instagramHandle?: string;
  facebookHandle?: string;
  // Extended fields for relationship types
  firstDate?: Date;
  anniversaryDate?: Date;
  anniversaryType?: string;
  loveLanguage?: string;
  friendshipStartDate?: Date;
  context?: string;
  insideJokesOrMemories?: string;
  lastHangoutDate?: Date;
  companyOrProject?: string;
  lastInteractionDate?: Date;
  preferredCommunicationStyle?: string;
  parentType?: string;
  healthNotes?: string;
  // Spouse specific fields
  weddingDate?: Date;
  marriageAnniversary?: Date;
  spousePreferences?: string;
}

export interface UserProfileData {
  name: string;
  email?: string;
  phoneNumber?: string;
  photoUrl?: string;
  country?: string;
  timezone?: string;
  preferredContactMethod?: "email" | "phone" | "sms";
  contactSyncEnabled?: boolean;
  syncedContactSources?: string[]; // Add this new field
  calendarSyncSettings?: {
    enabled: boolean;
    syncBirthdays: boolean;
    syncAnniversaries: boolean;
    syncCustomEvents: boolean;
  };
  onboardingCompleted: boolean;
  birthdate?: Date;
  loveLanguage?: string;
  giftingStyle?: GiftingStyle;
  reminderLeadTime?: number;
  preferredReminderTime?: string;
  reminderChannels?: ReminderChannel[];
  trackedContacts?: number;
  remindersSet?: number;
  lastLoginDate?: Date;
  averageCheckInInterval?: number;
  // Social media handles
  instagramHandle?: string;
  facebookHandle?: string;
}

// Extend the existing SuggestionType to include all types used in the app
export type SuggestionType = 
  | "reconnect" 
  | "birthday" 
  | "gift" 
  | "event" 
  | "message" 
  | "activity";

export interface SuggestionData {
  id: string;
  type: SuggestionType;
  title: string;
  description: string;
  contactName: string;
  contactAvatar?: string;
  dueDate?: string;
  actionLabel: string;
}

export interface ReminderData {
  id: string;
  contactId?: string;
  contactName?: string;
  contactAvatar?: string;
  title: string;
  date: string;
  type: string;
  notes?: string;
  leadTime?: number;
  isCompleted?: boolean;
}

// Make sure ImportantDate has a consistent definition
export interface ImportantDate {
  id?: string;
  type: string;
  label: string;
  date: string;
}

export interface Interaction {
  id: string;
  type: InteractionType;
  title?: string;
  date: string;
  content: string;
  photoUrls?: string[];
  contactId: string;
}

// Country to timezone mapping
export const countryToTimezone: TimeZoneMapping = {
  "United States": "GMT-5", // Eastern Time as default
  "Canada": "GMT-5",
  "United Kingdom": "GMT+0",
  "Australia": "GMT+10",
  "Germany": "GMT+1",
  "France": "GMT+1",
  "Japan": "GMT+9",
  "India": "GMT+5:30",
  "Brazil": "GMT-3",
  "Mexico": "GMT-6",
  "Spain": "GMT+1",
  "Italy": "GMT+1",
  "Netherlands": "GMT+1",
  "Sweden": "GMT+1",
  "Norway": "GMT+1",
  "China": "GMT+8",
  "South Korea": "GMT+9",
  "Singapore": "GMT+8",
  "New Zealand": "GMT+12",
  "South Africa": "GMT+2"
};
